import { CloudUploadOutlined, ErrorOutline } from "@mui/icons-material";
import { Alert, Button, Skeleton } from "@mui/material";
import { FileUploadElement, ParsedFileUploadElement } from "@types";
import { useEffect, useRef, useState } from "react";
import RawEditor, { RawEditorHandle } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import { FilePreview } from "src/components/Form/Elements/FilePreview";

type Props = {
  element: ParsedFileUploadElement | FileUploadElement;
  value?: string;
  onChange: (file: File) => void;
  allowedFileTypes: string | undefined;
  isLoading?: boolean;
  file?: File;
  fileId?: string | null;
  onDeleteFile?: () => void;
  filename?: string;
};

const UploadContainer = ({
  element,
  value,
  onChange,
  allowedFileTypes,
  isLoading,
  file,
  fileId,
  onDeleteFile,
  filename,
}: Props) => {
  const [uploadStatus, setUploadStatus] = useState("idle");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<RawEditorHandle>(null);

  // Reload editor content when value changes (e.g., after file upload)
  useEffect(() => {
    if (value) {
      editorRef.current?.reloadContent(value);
    }
  }, [value]);

  const handleFileChange = async (
    file: File | undefined,
    custom: (file: File) => void
  ) => {
    setUploadStatus("loading");
    if (!file) {
      setUploadStatus("failed");
      return;
    }
    try {
      await custom(file);
      setUploadStatus("completed");
    } catch (error) {
      setUploadStatus("failed");
      return;
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteFile = () => {
    setUploadStatus("idle");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onDeleteFile && onDeleteFile();
  };

  const hasFile = file && fileId && filename;

  return (
    <div key={element.id} className="flex flex-col gap-y-3">
      <Button
        component="label"
        role={undefined}
        variant="outlined"
        tabIndex={-1}
        startIcon={<CloudUploadOutlined />}
      >
        Upload file
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={allowedFileTypes || undefined}
          onChange={(e) => {
            handleFileChange(e.target.files?.[0], onChange);
          }}
        />
      </Button>

      {!isLoading ? (
        <div className="flex flex-col gap-3">
          {hasFile && (
            <FilePreview
              file={{ data: file, id: fileId, filename }}
              handleDeleteFile={handleDeleteFile}
            />
          )}
        </div>
      ) : (
        <Skeleton className="h-20 w-auto" />
      )}

      {/* This is show only for documents with n number of publications */}
      {uploadStatus !== "failed" && (uploadStatus === "completed" || value) && (
        <RawEditor
          ref={editorRef}
          data-testid={element.id}
          key={element.id}
          onChange={(_: string) => null}
          markdown={value || ""}
          hideToolbar={true}
        />
      )}

      {uploadStatus === "failed" && (
        <Alert icon={<ErrorOutline fontSize="inherit" />} severity="error">
          Failed to process the uploaded file.
        </Alert>
      )}
    </div>
  );
};

export default UploadContainer;
